import React, { useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useLanding } from '@/components/Landing/LandingKit';
import { PricingSection, PricingTitle } from './PricingKit';
import PageVideo from '@/components/Tutorials/PageVideo';
import BusinessSeatCalculator from '@/components/Business/BusinessSeatCalculator';
import BusinessCta from '@/components/Business/BusinessCta';
import useBusinessBrand from '@/components/Business/businessBrand';
import useBusinessEnrollment from '@/components/Business/useBusinessEnrollment';
import { businessCopy } from '@/constants/businessContent';

export default function PricingBusiness() {
  const { isCompact } = useLanding();
  const c = useBusinessBrand();
  const copy = businessCopy(useBusinessEnrollment());
  const sectionRef = useRef(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    let frame;
    const scrollToBusiness = () => {
      if (window.location.hash !== '#business') return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => sectionRef.current?.scrollIntoView?.({ block: 'start' }));
    };
    scrollToBusiness();
    window.addEventListener('hashchange', scrollToBusiness);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('hashchange', scrollToBusiness); };
  }, []);
  if (Platform.OS !== 'web') return null;
  return (
    <>
      <PricingSection label="Gab AI Business">
        <View ref={sectionRef} nativeID="business" style={{ flexDirection: isCompact ? 'column' : 'row', gap: 48, scrollMarginTop: 100, alignItems: isCompact ? 'stretch' : 'center' }}>
          <View style={{ flex: 1, gap: 16 }}>
            <Text weight="medium" style={{ color: c.muted, fontSize: 14 }}>Gab AI Business{copy.availability ? ` · ${copy.availability}` : ''}</Text>
            <PricingTitle>AI for your team. One company bill.</PricingTitle>
            <Text style={{ color: c.muted, fontSize: 17, lineHeight: 27, maxWidth: 470 }}>Bring your people together with Plus and Plus Premium seats, shared credits, and a clear view of company spending.</Text>
            <Text style={{ color: c.muted, fontSize: 15, lineHeight: 24 }}>{copy.pricingDetail}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              {copy.primary && <BusinessCta label={copy.primary.label} href={copy.primary.href} />}
              <BusinessCta label="Read the Business guide" href="/docs/business" secondary />
            </View>
          </View>
          <View style={{ flex: 1 }}><BusinessSeatCalculator /></View>
        </View>
      </PricingSection>
      <PricingSection label="See Gab AI at work">
        <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 30 }}>See Gab AI at work.</Text>
        <PageVideo page="business" />
      </PricingSection>
      <PricingSection label="What your team can hand to Gab AI">
        <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 14 }}>Hand it real work.</Text>
        <Text tag="p" style={{ color: c.muted, fontSize: 16, lineHeight: 26, marginBottom: 26 }}>Chat when you're thinking. Work when you're doing. A task keeps its files, its steps, and its history — nothing dead-ends in a chat thread.</Text>
        <View style={{ gap: 36 }}>
          <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
            <View style={{ flex: 1, gap: 12 }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Research &amp; reading</Text>
              <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Deep research, reading lists, and briefs, with sources to check, not confident guesses.</Text>
            </View>
            <View style={{ flex: 1, gap: 12 }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Decks, docs &amp; spreadsheets</Text>
              <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Presentations, reports, and working spreadsheets, made in Work and left yours to edit.</Text>
            </View>
          </View>
          <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
            <View style={{ flex: 1, gap: 12 }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Automations &amp; scripts</Text>
              <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Recurring tasks and small automations: gather data, update a sheet, keep the files tidy.</Text>
            </View>
            <View style={{ flex: 1, gap: 12 }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Code &amp; prototypes</Text>
              <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Working prototypes, small tools, and code that runs, built beside your people.</Text>
            </View>
          </View>
        </View>
      </PricingSection>
      <PricingSection label={copy.featuresLabel}>
        <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 30 }}>Built around your team</Text>
        <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
          {copy.features.map(([title, body]) => <View key={title} style={{ flex: 1, gap: 12 }}>
            <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>{title}</Text>
            <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>{body}</Text>
          </View>)}
        </View>
      </PricingSection>
    </>
  );
}
